import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  FormGroup,
  Label,
  Row,
  Badge,
  Button,
  Input,
} from "reactstrap";
import { Colxx, Separator } from "../../../components/common/CustomBootstrap";
import WorkingHours from "./WorkingHours";
import IntlMessages from "../../../helpers/IntlMessages";
import { injectIntl } from "react-intl";
import { NotificationManager } from "../../../components/common/react-notifications";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";

const Setting = ({
  onChangeWorkingHours,
  onAddWorkingHours,
  onRemoveWorkingHours,
  values,
  setting,
  intl,
}) => {
  // State cho các vị trí công việc
  const [positions, setPositions] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [newPosition, setNewPosition] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Get token for API calls
  const getAuthHeader = () => {
    const tokenStr = localStorage.getItem("access_token");
    return { headers: { Authorization: `Bearer ${tokenStr}` } };
  };

  // Load settings từ API khi mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${END_POINT}/user-staff/settings`,
        getAuthHeader(),
      );
      if (response.data && response.data.data) {
        const { staff_shifts, staff_positions } = response.data.data;
        setShifts(staff_shifts || []);
        setPositions(staff_positions || []);
      }
    } catch (err) {
      console.error("Error loading settings:", err);
      NotificationManager.error(
        intl.formatMessage({ id: "settings.staff.alert.load-error" }),
        "Error",
        3000,
      );
    } finally {
      setLoading(false);
    }
  };

  // Lưu settings lên API
  const saveSettings = async () => {
    try {
      setSaving(true);
      const response = await axios.put(
        `${END_POINT}/user-staff/settings`,
        {
          staff_shifts: shifts,
          staff_positions: positions,
        },
        getAuthHeader(),
      );
      if (response.data && response.data.status === 200) {
        NotificationManager.success(
          intl.formatMessage({ id: "settings.staff.alert.save-success" }),
          "Success",
          3000,
        );
      }
    } catch (err) {
      console.error("Error saving settings:", err);
      NotificationManager.error(
        intl.formatMessage({ id: "settings.staff.alert.save-error" }),
        "Error",
        3000,
      );
    } finally {
      setSaving(false);
    }
  };

  // Thêm vị trí mới
  const handleAddPosition = () => {
    if (newPosition.trim()) {
      // Kiểm tra trùng lặp
      if (!positions.includes(newPosition.trim())) {
        setPositions([...positions, newPosition.trim()]);
        setNewPosition("");
      } else {
        NotificationManager.warning(
          intl.formatMessage({ id: "settings.staff.alert.position-exists" }),
          "Warning",
          2000,
        );
      }
    }
  };

  // Xóa vị trí
  const handleRemovePosition = (posToRemove) => {
    setPositions(positions.filter((pos) => pos !== posToRemove));
  };

  // Thêm ca làm việc
  const handleAddShift = () => {
    const lastShift = shifts.length > 0 ? shifts[shifts.length - 1] : null;
    let startTime = "08:00";
    let endTime = "12:00";

    if (lastShift) {
      startTime = lastShift.end;
      const [lastHour, lastMinute] = lastShift.end.split(":").map(Number);
      let newEndHour = lastHour + 4;
      if (newEndHour >= 24) newEndHour = newEndHour - 24;
      endTime = `${newEndHour.toString().padStart(2, "0")}:${lastMinute.toString().padStart(2, "0")}`;
    }

    const newShift = {
      name: `Ca ${shifts.length + 1}`,
      start: startTime,
      end: endTime,
    };
    setShifts([...shifts, newShift]);
  };

  // Xóa ca làm việc
  const handleRemoveShift = (index) => {
    const newShifts = shifts.filter((_, idx) => idx !== index);
    // Cập nhật lại tên ca
    const updatedShifts = newShifts.map((shift, idx) => ({
      ...shift,
      name: `Ca ${idx + 1}`,
    }));
    setShifts(updatedShifts);
  };

  // Cập nhật giờ của ca
  const handleChangeShiftTime = (index, field, value) => {
    const newShifts = [...shifts];
    newShifts[index] = {
      ...newShifts[index],
      [field]: value,
      name: newShifts[index].name || `Ca ${index + 1}`,
    };
    setShifts(newShifts);
  };

  if (loading) {
    return (
      <Card className="mb-4">
        <CardBody className="text-center">
          <div className="loading" />
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardBody>
        {/* PHẦN 1: CA LÀM VIỆC */}
        <Row className="mb-4">
          <Colxx xxs="12">
            <h6 className="mb-3">
              <i className="simple-icon-clock mr-2"></i>
              <IntlMessages id="settings.staff.shift-title" />
            </h6>
            <p className="text-muted text-small mb-3">
              <IntlMessages id="settings.staff.shift-desc" />
            </p>
          </Colxx>

          {/* Danh sách ca làm việc */}
          <Colxx xxs="12">
            {shifts.map((shift, idx) => (
              <div
                key={idx}
                className="mb-3 p-3"
                style={{
                  // border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  // backgroundColor: "#f9f9f9",
                }}
              >
                <Row className="mb-2">
                  <Colxx xxs="12">
                    <Badge
                      color="primary"
                      style={{ fontSize: "13px", padding: "6px 12px" }}
                    >
                      {shift.name}
                    </Badge>
                    <span
                      className="ml-2 text-danger"
                      style={{ cursor: "pointer", fontWeight: "bold" }}
                      onClick={() => handleRemoveShift(idx)}
                    >
                      × <IntlMessages id="settings.staff.delete" />
                    </span>
                  </Colxx>
                </Row>
                <Row>
                  <Colxx xxs="5">
                    <Label className="text-muted text-small">
                      <IntlMessages id="settings.staff.from" />
                    </Label>
                    <Input
                      type="time"
                      value={shift.start}
                      onChange={(e) =>
                        handleChangeShiftTime(idx, "start", e.target.value)
                      }
                    />
                  </Colxx>
                  <Colxx xxs="5">
                    <Label className="text-muted text-small">
                      <IntlMessages id="settings.staff.to" />
                    </Label>
                    <Input
                      type="time"
                      value={shift.end}
                      onChange={(e) =>
                        handleChangeShiftTime(idx, "end", e.target.value)
                      }
                    />
                  </Colxx>
                </Row>
              </div>
            ))}
            <Button color="outline-primary" onClick={handleAddShift}>
              + <IntlMessages id="settings.staff.add-shift" />{" "}
              {shifts.length + 1}
            </Button>
          </Colxx>
        </Row>

        <Separator className="mb-4" />

        {/* PHẦN 2: VỊ TRÍ CÔNG VIỆC */}
        <Row>
          <Colxx xxs="12">
            <h6 className="mb-3">
              <i className="simple-icon-people mr-2"></i>
              <IntlMessages id="settings.staff.position-title" />
            </h6>
            <p className="text-muted text-small mb-3">
              <IntlMessages id="settings.staff.position-desc" />
            </p>
          </Colxx>

          {/* Danh sách vị trí hiện tại */}
          <Colxx xxs="12" className="mb-3">
            <div className="d-flex flex-wrap">
              {positions.map((pos, idx) => (
                <Badge
                  key={idx}
                  color="primary"
                  className="mr-2 mb-2 p-2"
                  style={{ fontSize: "13px" }}
                >
                  {pos}
                  <span
                    className="ml-2"
                    style={{ cursor: "pointer", fontWeight: "bold" }}
                    onClick={() => handleRemovePosition(pos)}
                  >
                    ×
                  </span>
                </Badge>
              ))}
            </div>
          </Colxx>

          {/* Thêm vị trí mới */}
          <Colxx xxs="12" sm="6" md="4">
            <FormGroup>
              <Label className="text-muted">
                <IntlMessages id="settings.staff.add-position" />
              </Label>
              <div className="d-flex">
                <Input
                  type="text"
                  value={newPosition}
                  onChange={(e) => setNewPosition(e.target.value)}
                  placeholder={intl.formatMessage({
                    id: "settings.staff.position-placeholder",
                  })}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddPosition();
                    }
                  }}
                />
                <Button
                  color="primary"
                  className="ml-2"
                  onClick={handleAddPosition}
                >
                  <i className="simple-icon-plus"></i>
                </Button>
              </div>
            </FormGroup>
          </Colxx>
        </Row>

        <Separator className="my-4" />

        {/* Nút Lưu */}
        <Row>
          <Colxx xxs="12" className="text-right">
            <Button
              color="primary"
              size="lg"
              onClick={saveSettings}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2"></span>
                  <IntlMessages id="settings.staff.saving" />
                </>
              ) : (
                <>
                  <i className="simple-icon-check mr-2"></i>
                  <IntlMessages id="settings.staff.save-btn" />
                </>
              )}
            </Button>
          </Colxx>
        </Row>
      </CardBody>
    </Card>
  );
};

export default injectIntl(Setting);
